import { Avatar, Layout, Breadcrumb, Tooltip } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { UserOutlined, LogoutOutlined } from "@ant-design/icons";
import { SiderNavigationComponent } from "../../components/main/sider/SiderNavigationComponent";
import { SiderTitleComponent } from "../../components/main/sider/SiderTitleComponent";
import { ProfileDrawerComponent } from "../../components/main/profile/ProfileDrawerComponent";
import "./appShell.css";

const { Header, Content, Footer, Sider } = Layout;

type AppLayoutProps = {
  contextHolder: any;
  loggedInUserInfo: any;
  onClickViewProfile: () => void;
  openDrawerStatus: boolean;
  onClickLogout: () => void;
  generalInfo: any;
  roleInfo: any;
  userPopDownStatus: boolean;
  onHandleHoverChange: () => void;
  route: any;
};

export const AppLayoutIndexComponent = ({
  contextHolder,
  loggedInUserInfo,
  onClickViewProfile,
  openDrawerStatus,
  onClickLogout,
  generalInfo,
  roleInfo,
  userPopDownStatus,
  onHandleHoverChange,
  route,
}: AppLayoutProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Parse user display info
  const getUserDisplayInfo = () => {
    if (loggedInUserInfo?.fullName) {
      return {
        name: loggedInUserInfo.fullName,
        role: loggedInUserInfo.roleName || "Admin",
      };
    }
    if (generalInfo) {
      try {
        const parsed = typeof generalInfo === "string" ? JSON.parse(generalInfo) : generalInfo;
        const name = [parsed.first_name, parsed.last_name].filter(Boolean).join(" ") || "User";
        const role = roleInfo ? (typeof roleInfo === "string" ? JSON.parse(roleInfo) : roleInfo)?.name || "Admin" : "Admin";
        return { name, role };
      } catch {
        return { name: "User", role: "Admin" };
      }
    }
    return { name: "User", role: "Admin" };
  };

  const { name: userName, role: userRole } = getUserDisplayInfo();

  const crumbs: Array<{ path: string; breadcrumbName: string }> =
    route.breadCrumbs ?? (route.title ? [{ path: "", breadcrumbName: route.title }] : []);

  return (
    <Layout>
      {contextHolder}
      <ProfileDrawerComponent
        loggedInUserInfo={loggedInUserInfo}
        onClickViewProfile={onClickViewProfile}
        openDrawerStatus={openDrawerStatus}
        key={"profile-drawer"}
      />
      <Sider
        className="app-sider"
        theme="light"
        breakpoint="lg"
        collapsible={true}
        collapsed={isCollapsed}
        onCollapse={(collapsed) => setIsCollapsed(collapsed)}
        width={248}
        collapsedWidth={72}
      >
        <SiderTitleComponent collapsed={isCollapsed} />
        <SiderNavigationComponent />

        <div className="app-user" data-testid="app-user">
          <Tooltip title="View profile" placement="right">
            <button
              type="button"
              className="app-user-profile"
              onClick={onClickViewProfile}
              data-testid="app-user-profile"
            >
              <Avatar size={32} icon={<UserOutlined />} className="app-avatar" />
              {!isCollapsed && (
                <span className="app-user-text">
                  <span className="app-user-name">{userName}</span>
                  <span className="app-user-role">{userRole}</span>
                </span>
              )}
            </button>
          </Tooltip>
          {!isCollapsed && (
            <Tooltip title="Log out" placement="top">
              <button
                type="button"
                className="app-icon-button"
                onClick={onClickLogout}
                aria-label="Log out"
                data-testid="app-logout"
              >
                <LogoutOutlined />
              </button>
            </Tooltip>
          )}
        </div>
      </Sider>

      <Layout className="app-main">
        <Header className="app-header">
          <Breadcrumb separator=">" className="app-breadcrumb">
            {crumbs.map((item, index) => (
              <Breadcrumb.Item key={`${item.breadcrumbName}-${index}`}>
                {index < crumbs.length - 1 && item.path ? (
                  <Link to={item.path}>{item.breadcrumbName}</Link>
                ) : (
                  item.breadcrumbName
                )}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>

          <Tooltip title="View profile">
            <button
              type="button"
              className="app-header-avatar"
              onClick={onClickViewProfile}
              aria-label="View profile"
              data-testid="app-header-profile"
            >
              <Avatar size={32} icon={<UserOutlined />} className="app-avatar" />
            </button>
          </Tooltip>
        </Header>

        <Content className="app-content">
          <div className="app-content-inner">
            <route.component />
          </div>
        </Content>

        <Footer className="app-footer">Automart Installment Admin ©2024</Footer>
      </Layout>
    </Layout>
  );
};
